import taapi from './taapi'

// Template-management API surface - wraps the `/api2/sm*` (style/tweak groups)
// and `/api2/be*` (scoring/win-theme elements) endpoints. Callers live in
// src/tabs/templates/* and (for the initial load) src/pages/AppPage.tsx and
// src/tabs/TemplatesTab.tsx. Payload shapes are preserved exactly - the goal
// here is centralisation, not a behaviour change.

// ─── /api2/sminit ────────────────────────────────────────────────────────────

export interface SmInit {
  stylegroups?: { name: string }[]
  elementgroups?: any[]
  [k: string]: any
}

export async function fetchSmInit(): Promise<SmInit | null> {
  return (await taapi.get('/api2/sminit')) as SmInit | null
}

// ─── /api2/smsavestylegroup ──────────────────────────────────────────────────
// Used for both style templates (type `aii`) and tweak groups (type `els`).
// The backend distinguishes them via the per-tag `type` field; callers
// already build the payload correctly.

export async function saveStyleGroup(payload: {
  name: string
  styletags: any[]
  [k: string]: any
}): Promise<void> {
  await taapi.post('/api2/smsavestylegroup', payload)
}

// ─── /api2/smrename ──────────────────────────────────────────────────────────
// `type` is the *group* type - `aii` for style templates, `els` for tweak
// groups. The styles tab passes `'aii'`; the tweaks tab passes `'els'`.

export async function renameStyleGroup(origname: string, newname: string, type: string): Promise<void> {
  await taapi.post('/api2/smrename', { origname, newname, type })
}

// ─── /api2/smdelete ──────────────────────────────────────────────────────────

export async function deleteStyleGroup(stylename: string, type: string): Promise<void> {
  await taapi.post('/api2/smdelete', { stylename, type })
}

// ─── /api2/smdeltag ──────────────────────────────────────────────────────────

export async function deleteStyleTag(tagsha: string): Promise<void> {
  await taapi.post('/api2/smdeltag', { tagsha })
}

// ─── /api2/besaveelement ─────────────────────────────────────────────────────
// Saves a scoring template or win-theme set. The element's `type` field
// (`'score'` | `'wintheme'`) discriminates between the two. New elements omit
// any id field; the backend assigns one and returns the saved element.

export async function saveElement(element: Record<string, any>): Promise<Record<string, any> | null> {
  return await taapi.post('/api2/besaveelement', element) as Record<string, any> | null
}

// ─── /api2/bedelelement ──────────────────────────────────────────────────────

export async function deleteElement(element: Record<string, any>): Promise<void> {
  await taapi.post('/api2/bedelelement', element)
}
